import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams, Link } from 'react-router-dom'
import { Sparkles, Send, Calendar, X as XIcon, Trash2, Users, Ticket } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { PerformanceScopeBanner } from '@/components/PerformanceScopeBanner'
import { usePerformanceScope } from '@/lib/use-performance-scope'
import { useFeatureEnabled } from '@/lib/use-feature-enabled'
import { cn } from '@/lib/utils'
import { BrokersSection } from '@/pages/BrokersPage'
import { TicketingMarketingSection } from '@/components/TicketingMarketingSection'

type Platform =
  | 'meta_facebook'
  | 'instagram'
  | 'youtube'
  | 'tiktok'
  | 'linkedin'
  | 'x'
  | 'bluesky'
  | 'website'

type PostStatus =
  | 'draft'
  | 'scheduled'
  | 'publishing'
  | 'published'
  | 'failed'
  | 'cancelled'

type MarketingTab = 'brokers' | 'ticketing' | 'events'

interface MarketingPost {
  id: string
  showId: string | null
  platform: Platform
  subject: string | null
  body: string
  scheduledFor: string | null
  publishedAt: string | null
  externalUrl: string | null
  status: PostStatus
  aiGenerated: boolean
  failureReason: string | null
  createdAt: string
  updatedAt: string
}

interface ListResponse {
  items?: MarketingPost[]
}

interface ShowOption {
  id: string
  title: string
  date: string
  venue: string
  city: string
}

const PLATFORM_LABEL: Record<Platform, string> = {
  meta_facebook: 'Facebook',
  instagram: 'Instagram',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  x: 'X',
  bluesky: 'Bluesky',
  website: 'Website',
}

const PLATFORM_COLOUR: Record<Platform, string> = {
  meta_facebook: 'bg-blue-100 text-blue-800',
  instagram: 'bg-pink-100 text-pink-800',
  youtube: 'bg-red-100 text-red-800',
  tiktok: 'bg-slate-200 text-slate-900',
  linkedin: 'bg-sky-100 text-sky-800',
  x: 'bg-slate-200 text-slate-900',
  bluesky: 'bg-cyan-100 text-cyan-800',
  website: 'bg-amber-100 text-amber-800',
}

const STATUS_COLOUR: Record<PostStatus, string> = {
  draft: 'bg-slate-100 text-slate-700',
  scheduled: 'bg-amber-100 text-amber-700',
  publishing: 'bg-indigo-100 text-indigo-700',
  published: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-rose-100 text-rose-700',
  cancelled: 'bg-rose-100 text-rose-700',
}

function useModuleEnabled(feature: string): boolean {
  return useFeatureEnabled(feature)
}

export function MarketingPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const brokerEnabled = useModuleEnabled('broker')
  const ticketingEnabled = useModuleEnabled('ticketing')
  const eventsEnabled = useModuleEnabled('marketing')

  const requestedTab = searchParams.get('tab')
  const tab: MarketingTab =
    requestedTab === 'events' && eventsEnabled
      ? 'events'
      : requestedTab === 'ticketing' && ticketingEnabled
        ? 'ticketing'
        : requestedTab === 'brokers' && brokerEnabled
          ? 'brokers'
          : brokerEnabled
            ? 'brokers'
            : ticketingEnabled
              ? 'ticketing'
              : 'events'

  function setTab(next: MarketingTab) {
    setSearchParams(next === 'brokers' ? {} : { tab: next }, { replace: true })
  }

  const enabledTabs = [
    brokerEnabled && 'brokers',
    ticketingEnabled && 'ticketing',
    eventsEnabled && 'events',
  ].filter(Boolean) as MarketingTab[]

  return (
    <div>
      <PageHeader
        title="Marketing"
        subtitle="Brokers, HiveCells tier pre-releases, and event promotion."
      />

      {enabledTabs.length === 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Broker, ticketing, and event marketing modules are turned off for this artist.
          Enable them under{' '}
          <Link to="/settings/artist" className="font-medium underline">
            Artist settings & modules
          </Link>{' '}
          or{' '}
          <Link to="/org/settings" className="font-medium underline">
            Org settings
          </Link>
          .
        </div>
      )}

      {enabledTabs.length > 1 && (
        <div className="mb-6 flex flex-col gap-1 rounded-lg border border-ovation-ink/10 bg-white p-1 sm:flex-row">
          {brokerEnabled && (
            <TabButton
              active={tab === 'brokers'}
              onClick={() => setTab('brokers')}
              icon={<Users className="h-4 w-4" />}
              label="Brokers & coverage"
            />
          )}
          {ticketingEnabled && (
            <TabButton
              active={tab === 'ticketing'}
              onClick={() => setTab('ticketing')}
              icon={<Ticket className="h-4 w-4" />}
              label="Ticketing & tiers"
            />
          )}
          {eventsEnabled && (
            <TabButton
              active={tab === 'events'}
              onClick={() => setTab('events')}
              icon={<Sparkles className="h-4 w-4" />}
              label="Event marketing"
            />
          )}
        </div>
      )}

      {tab === 'brokers' && brokerEnabled && <BrokersSection />}
      {tab === 'ticketing' && ticketingEnabled && <TicketingMarketingSection />}
      {tab === 'events' && eventsEnabled && <EventMarketingSection />}
    </div>
  )
}

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
        active
          ? 'bg-ovation-ink text-ovation-paper'
          : 'text-ovation-muted hover:bg-ovation-ink/5 hover:text-ovation-ink',
      )}
    >
      {icon}
      {label}
    </button>
  )
}

function EventMarketingSection() {
  const qc = useQueryClient()
  const scope = usePerformanceScope()

  const postsQuery = useQuery<MarketingPost[], ApiClientError>({
    queryKey: ['marketing', ...scope.queryKeySuffix],
    queryFn: async () => {
      const res = await artistFetch<MarketingPost[] | ListResponse>(
        `/marketing${scope.toQueryString()}`,
      )
      if (Array.isArray(res)) return res
      return res.items ?? []
    },
  })
  const posts = scope.filterByShowIds(postsQuery.data ?? [], (p) => p.showId)

  const showsQuery = useQuery<ShowOption[], ApiClientError>({
    queryKey: ['marketing-shows'],
    queryFn: () => artistFetch<ShowOption[]>('/ticketing/shows'),
  })

  const [generateShowId, setGenerateShowId] = useState('')
  const [generating, setGenerating] = useState(false)
  const [generateError, setGenerateError] = useState<string | null>(null)

  async function onGenerate(e: FormEvent) {
    e.preventDefault()
    if (!generateShowId) return
    setGenerating(true)
    setGenerateError(null)
    try {
      await artistFetch('/marketing/generate', {
        method: 'POST',
        body: { showId: generateShowId },
      })
      qc.invalidateQueries({ queryKey: ['marketing'] })
    } catch (err) {
      setGenerateError(
        err instanceof ApiClientError ? err.message : 'Unexpected error.',
      )
    } finally {
      setGenerating(false)
    }
  }

  async function publishNow(postId: string) {
    try {
      await artistFetch(`/marketing/${postId}/publish`, { method: 'POST' })
      qc.invalidateQueries({ queryKey: ['marketing'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    }
  }

  async function cancelPost(postId: string) {
    try {
      await artistFetch(`/marketing/${postId}/cancel`, { method: 'POST' })
      qc.invalidateQueries({ queryKey: ['marketing'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    }
  }

  async function removePost(postId: string) {
    if (!confirm('Delete this draft?')) return
    try {
      await artistFetch(`/marketing/${postId}`, { method: 'DELETE' })
      qc.invalidateQueries({ queryKey: ['marketing'] })
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    }
  }

  const byShow = new Map<string | 'standalone', MarketingPost[]>()
  for (const p of posts) {
    const key = p.showId ?? 'standalone'
    if (!byShow.has(key)) byShow.set(key, [])
    byShow.get(key)!.push(p)
  }

  const showById = new Map((showsQuery.data ?? []).map((s) => [s.id, s]))

  return (
    <>
      <PerformanceScopeBanner />

      <form
        onSubmit={onGenerate}
        className="mb-6 flex flex-wrap items-end gap-3 rounded-lg border border-ovation-ink/10 bg-white p-4"
      >
        <div className="min-w-[200px] flex-1">
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
            Generate AI drafts for show
          </label>
          <select
            value={generateShowId}
            onChange={(e) => setGenerateShowId(e.target.value)}
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
          >
            <option value="">— pick a show —</option>
            {(showsQuery.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {new Date(s.date).toLocaleDateString()} — {s.venue}, {s.city}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          disabled={!generateShowId || generating}
          className="flex items-center gap-2 rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
        >
          <Sparkles className="h-4 w-4" />
          {generating ? 'Generating…' : 'Generate copy'}
        </button>
        {generateError && (
          <div className="basis-full text-sm text-red-700">{generateError}</div>
        )}
        <p className="basis-full text-xs text-ovation-muted">
          Drops AI-written drafts for every default platform (Instagram,
          Facebook, X, Bluesky, LinkedIn). Idempotent — won&apos;t duplicate posts
          for platforms already drafted.
        </p>
      </form>

      <AsyncBoundary isLoading={postsQuery.isLoading} error={postsQuery.error}>
        {posts.length === 0 ? (
          <div className="rounded-md border border-ovation-ink/10 bg-white p-6 text-sm text-ovation-muted">
            No marketing posts yet. Confirm an inquiry or pick a show above to
            auto-generate the first batch.
          </div>
        ) : (
          <div className="space-y-6">
            {[...byShow.entries()].map(([key, group]) => {
              const show = key !== 'standalone' ? showById.get(key) : undefined
              return (
                <div key={key} className="space-y-3">
                  <div className="flex items-baseline justify-between gap-3 border-b border-ovation-ink/10 pb-2">
                    <h2 className="font-display text-xl">
                      {show
                        ? `${show.venue}, ${show.city} — ${new Date(show.date).toLocaleDateString()}`
                        : 'Standalone posts'}
                    </h2>
                    <span className="text-xs text-ovation-muted">
                      {group.length} post{group.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                    {group.map((post) => (
                      <PostCard
                        key={post.id}
                        post={post}
                        onPublishNow={() => publishNow(post.id)}
                        onCancel={() => cancelPost(post.id)}
                        onDelete={() => removePost(post.id)}
                      />
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </AsyncBoundary>
    </>
  )
}

function PostCard({
  post,
  onPublishNow,
  onCancel,
  onDelete,
}: {
  post: MarketingPost
  onPublishNow: () => void
  onCancel: () => void
  onDelete: () => void
}) {
  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white p-4">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              'inline-flex rounded px-2 py-0.5 text-xs',
              PLATFORM_COLOUR[post.platform],
            )}
          >
            {PLATFORM_LABEL[post.platform]}
          </span>
          <span
            className={cn(
              'inline-flex rounded px-2 py-0.5 text-xs',
              STATUS_COLOUR[post.status],
            )}
          >
            {post.status}
          </span>
          {post.aiGenerated && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-ovation-muted">
              <Sparkles className="h-3 w-3" />
              AI
            </span>
          )}
        </div>
      </div>

      {post.subject && (
        <div className="mb-1 text-sm font-medium">{post.subject}</div>
      )}
      <pre className="mb-3 whitespace-pre-wrap font-sans text-sm text-ovation-ink">
        {post.body}
      </pre>

      {post.scheduledFor && post.status === 'scheduled' && (
        <div className="mb-2 flex items-center gap-1 text-xs text-amber-700">
          <Calendar className="h-3 w-3" />
          Scheduled for {new Date(post.scheduledFor).toLocaleString()}
        </div>
      )}
      {post.publishedAt && (
        <div className="mb-2 text-xs text-emerald-700">
          Published {new Date(post.publishedAt).toLocaleString()}{' '}
          {post.externalUrl && (
            <a
              href={post.externalUrl}
              target="_blank"
              rel="noreferrer"
              className="ml-1 underline"
            >
              View
            </a>
          )}
        </div>
      )}
      {post.failureReason && (
        <div className="mb-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
          {post.failureReason}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {(post.status === 'draft' || post.status === 'failed') && (
          <button
            type="button"
            onClick={onPublishNow}
            className="flex items-center gap-1 rounded-md bg-ovation-ink px-3 py-1.5 text-xs font-medium text-ovation-paper hover:bg-ovation-ink/90"
          >
            <Send className="h-3 w-3" />
            Publish now
          </button>
        )}
        {(post.status === 'draft' || post.status === 'scheduled') && (
          <button
            type="button"
            onClick={onCancel}
            className="flex items-center gap-1 rounded-md border border-ovation-ink/15 px-3 py-1.5 text-xs font-medium text-ovation-ink hover:bg-ovation-ink/5"
          >
            <XIcon className="h-3 w-3" />
            Cancel
          </button>
        )}
        {post.status === 'draft' && (
          <button
            type="button"
            onClick={onDelete}
            className="flex items-center gap-1 rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
          >
            <Trash2 className="h-3 w-3" />
            Delete
          </button>
        )}
      </div>
    </div>
  )
}
